import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import UploadZone from './components/UploadZone';
import ProcessingPanel from './components/ProcessingPanel';
import ExtractedData from './components/ExtractedData';
import OutputOptions from './components/OutputOptions';
import PdfPreviewPanel from './components/PdfPreviewPanel';
import Toast from './components/Toast';
import PageFooter from './components/PageFooter';
import {
  generatePDF,
  bulkPackZip,
  bulkZipFilename,
  outputPdfFilename,
  createPdfPreviewUrl,
  manifestPdfArcname,
  manifestSuccessItems,
  manifestFailedItems,
  summarizeBulkZohoStatus,
  formatBulkZohoSummary,
  loadZipFromBlob,
  readPdfFromZip,
  login,
  logout,
  getStoredToken,
  getActiveBulkJob,
  abortAllServerJobs,
  clearActiveBulkJob,
  getBulkJobManifest,
  ensureFullManifestItems,
  manifestItemsHaveReviewData,
  normalizeAsyncJobResult,
  fetchBulkJobPdf,
  fetchBulkJobZip,
  persistActiveBulkJob,
  AUTH_EXPIRED_EVENT,
} from './api';

// STAGE: "upload" | "processing" | "review" | "export"
const STAGES = ['upload', 'processing', 'review', 'export'];

const styles = {
  app: {
    minHeight: '100vh',
    background: 'var(--bg)',
    display: 'flex',
    flexDirection: 'column',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    minWidth: 0,
  },
  logoImg: {
    height: 36,
    width: 'auto',
    maxWidth: 'min(260px, 48vw)',
    objectFit: 'contain',
    display: 'block',
    flexShrink: 0,
  },
  stepNav: {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
  },
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  stepDot: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    border: '1.5px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    fontWeight: 700,
    transition: 'all 0.2s',
  },
  stepLabel: {
    fontSize: 11,
  },
  stepDivider: {
    width: 20,
    height: 1,
    background: 'var(--border)',
    margin: '0 8px',
  },
  topBarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
    flexShrink: 0,
  },
  logoutBtn: {
    /* styled via .app-topbar__logout */
  },
  confirmOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'var(--overlay)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    zIndex: 2000,
  },
  confirmCard: {
    width: '100%',
    maxWidth: 360,
    padding: '28px 32px',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border)',
    background: 'var(--bg-card)',
    boxShadow: '0 12px 40px var(--shadow)',
  },
  confirmTitle: {
    fontFamily: 'var(--font-display)',
    fontWeight: 700,
    fontSize: 17,
    color: 'var(--text-primary)',
    marginBottom: 10,
  },
  confirmMessage: {
    fontFamily: 'var(--font-mono)',
    fontSize: 13,
    color: 'var(--text-secondary)',
    lineHeight: 1.5,
    marginBottom: 24,
  },
  confirmActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
  },
  main: {
    flex: 1,
    display: 'flex',
    maxWidth: 1140,
    margin: '0 auto',
    width: '100%',
    padding: '32px 32px 48px',
    gap: 40,
  },
  left: {
    width: 240,
    flexShrink: 0,
    paddingTop: 8,
  },
  right: {
    flex: 1,
    minWidth: 0,
  },
  pageTitle: {
    fontFamily: 'var(--font-display)',
    fontWeight: 700,
    fontSize: 26,
    color: 'var(--text-heading)',
    lineHeight: 1.2,
    marginBottom: 8,
    letterSpacing: '-0.02em',
  },
  pageDesc: {
    fontSize: 13,
    color: 'var(--text-secondary)',
    lineHeight: 1.65,
    marginBottom: 28,
  },
  sideInfo: {
    marginTop: 32,
  },
  sideLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  sideList: {
    listStyle: 'none',
    padding: 0,
  },
  sideListItem: {
    fontSize: 12,
    color: 'var(--text-secondary)',
    padding: '6px 0',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  sideListDot: {
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: 'var(--green)',
    flexShrink: 0,
  },
  divider: {
    height: 1,
    background: 'var(--border)',
    margin: '24px 0',
  },
  actionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 24,
  },
  btnPrimary: {
    padding: '11px 22px',
    borderRadius: 'var(--radius-md)',
    border: 'none',
    background: 'var(--green)',
    color: 'var(--white)',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    transition: 'filter 0.15s',
    boxShadow: '0 2px 8px rgba(102, 211, 19, 0.3)',
  },
  btnLoading: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  resetBtn: {
    background: 'none',
    border: 'none',
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    color: 'var(--text-tertiary)',
    cursor: 'pointer',
    padding: '4px 0',
    marginTop: 12,
    display: 'block',
    transition: 'color 0.15s',
  },
  statusBadge: {
    /* use className status-pill */
  },
  outputChoiceReminder: {
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    color: 'var(--text-secondary)',
    marginBottom: 20,
    lineHeight: 1.5,
  },
  outputChoiceReminderStrong: {
    color: 'var(--text-primary)',
    fontWeight: 600,
  },
  bulkToggleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 20,
    padding: '14px 16px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border)',
    background: 'var(--bg-card)',
    boxShadow: 'var(--card-shadow)',
    cursor: 'pointer',
    userSelect: 'none',
  },
  bulkToggleRowActive: {
    borderColor: 'var(--blue)',
    background: 'var(--brand-dim)',
    boxShadow: '0 0 0 3px rgba(38, 119, 255, 0.08)',
  },
  bulkToggleText: {
    flex: 1,
    minWidth: 0,
  },
  bulkToggleTitle: {
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    fontSize: 13,
    color: 'var(--text-primary)',
    marginBottom: 4,
  },
  bulkToggleDesc: {
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    color: 'var(--text-tertiary)',
    lineHeight: 1.5,
  },
  bulkTabPanel: {
    marginTop: 16,
  },
  bulkStatusBanner: {
    marginBottom: 20,
    padding: '14px 16px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border)',
    background: 'var(--bg-card)',
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    lineHeight: 1.55,
    color: 'var(--text-secondary)',
  },
  bulkStatusBannerWarn: {
    borderColor: 'var(--accent-border)',
    background: 'var(--accent-dim)',
  },
  bulkStatusTitle: {
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    fontSize: 13,
    color: 'var(--text-primary)',
    marginBottom: 8,
  },
  bulkStatusList: {
    margin: '8px 0 0',
    paddingLeft: 18,
  },
  bulkStatusListItem: {
    marginBottom: 4,
  },
};

const STAGE_LABELS = ['Upload', 'Processing', 'Review', 'Export'];

const OUTPUT_TYPE_LABELS = {
  standard: 'Standard',
  watermark: 'Watermarked',
  masked: 'Masked',
  all: 'All formats',
};

// Keep in sync with backend/field_schema.py SHEET_HEADERS (Zoho Sheet auto-save)
const SHEET_COLUMN_LABELS = [
  'Name of Client',
  'City',
  'Date',
  'Name of creditor (Bank)',
  'Type of settlement',
  'Loan Type',
  'Total outstanding debt',
  'Settlement amount',
  'Percent of discount from outstanding',
];

function ConfirmDialog({ title, message, confirmLabel, cancelLabel, onConfirm, onCancel }) {
  return (
    <div style={styles.confirmOverlay} onClick={onCancel} role="presentation">
      <div
        style={styles.confirmCard}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        onClick={e => e.stopPropagation()}
      >
        <div id="confirm-dialog-title" style={styles.confirmTitle}>{title}</div>
        <p style={styles.confirmMessage}>{message}</p>
        <div style={styles.confirmActions}>
          <button type="button" style={styles.btnPrimary} onClick={onCancel}>
            {cancelLabel}
          </button>
          <button type="button" style={styles.btnPrimary} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const loginStyles = {
  shell: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--bg)',
  },
  body: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px 24px',
  },
  card: {
    padding: 40,
    border: '1px solid var(--border)',
    borderRadius: 12,
    width: '100%',
    maxWidth: 360,
    background: 'var(--bg-card)',
    boxShadow: 'var(--card-shadow)',
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontWeight: 700,
    fontSize: 18,
    color: 'var(--text-heading)',
    marginBottom: 24,
  },
  field: {
    /* use .field-input */
  },
  passwordWrap: {
    position: 'relative',
    marginBottom: 8,
  },
  passwordInput: {
    width: '100%',
    paddingRight: 72,
    boxSizing: 'border-box',
  },
  showPasswordBtn: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    padding: '4px 8px',
    border: 'none',
    borderRadius: 6,
    background: 'transparent',
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-mono)',
    fontSize: 10,
    letterSpacing: '0.04em',
    cursor: 'pointer',
  },
};

function LoginGate({ children }) {
  const [loggedIn, setLoggedIn] = useState(() => Boolean(getStoredToken()));
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!username.trim() || !password) {
      setError('Please enter username and password.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await login(username.trim(), password);
      setLoggedIn(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid username or password.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = useCallback(async () => {
    await logout();
    setLoggedIn(false);
    setUsername('');
    setPassword('');
    setError('');
  }, []);

  useEffect(() => {
    const onAuthExpired = () => {
      abortAllServerJobs();
      setLoggedIn(false);
      setError('Your session expired. Please sign in again.');
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
  }, []);

  if (loggedIn) return React.cloneElement(children, { onLogout: handleLogout });

  return (
    <div style={loginStyles.shell}>
      <header className="app-topbar" style={styles.topBar}>
        <div style={styles.logo}>
          <div className="app-topbar__logo-badge">
            <img
              src={`${process.env.PUBLIC_URL}/sml-logo.png`}
              alt="Settle My Loan"
            />
          </div>
          <div className="app-topbar__subtitle">
            financial document<br />processor
          </div>
        </div>
      </header>

      <div style={loginStyles.body}>
        <div style={loginStyles.card}>
          <div style={loginStyles.title}>Sign in</div>
          <input
            placeholder="Username"
            value={username}
            autoComplete="username"
            onChange={e => setUsername(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleLogin()}
            disabled={loading}
            className="field-input"
            style={{ marginBottom: 12 }}
          />
          <div style={loginStyles.passwordWrap}>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              autoComplete="current-password"
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
              disabled={loading}
              className="field-input"
              style={loginStyles.passwordInput}
            />
            <button
              type="button"
              onClick={() => setShowPassword(v => !v)}
              disabled={loading}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              style={{
                ...loginStyles.showPasswordBtn,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
              }}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          {error && (
            <div style={{ color: 'var(--danger)', fontSize: 12, marginBottom: 14, fontFamily: 'var(--font-mono)' }}>
              {error}
            </div>
          )}
          <button
            onClick={handleLogin}
            disabled={loading}
            className="btn-primary"
            style={{
              width: '100%',
              marginTop: 8,
              justifyContent: 'center',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </div>
      </div>
      <PageFooter />
    </div>
  );
}


function App({ onLogout }) {
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [stage, setStage] = useState('upload');
  const [file, setFile] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [summary, setSummary] = useState('');
  const [outputType, setOutputType] = useState('all');
  const [masked, setMasked] = useState(false);
  const showMaskToggle = outputType === 'masked';

  useEffect(() => {
    if (outputType !== 'masked') {
      setMasked(false);
    }
  }, [outputType]);

  const [downloading, setDownloading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [reviewPanel, setReviewPanel] = useState('data');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [toast, setToast] = useState(null);

  const [bulkZipMode, setBulkZipMode] = useState(false);
  const [bulkPackFiles, setBulkPackFiles] = useState([]);
  const [bulkManifest, setBulkManifest] = useState(null);
  const [bulkReviewTab, setBulkReviewTab] = useState(0);
  const [processingKey, setProcessingKey] = useState(0);
  const [bulkJobProgress, setBulkJobProgress] = useState(null);
  const [bulkBatchStats, setBulkBatchStats] = useState(null);
  const bulkPackCountRef = useRef(0);
  const bulkZipBlobRef = useRef(null);
  const bulkZipDownloadNameRef = useRef(null);
  const bulkManifestRef = useRef(null);
  const extractedDataRef = useRef(null);
  const singlePdfBlobRef = useRef(null);
  const singlePdfFilenameRef = useRef(null);
  const processedOutputFilenameRef = useRef(null);
  const previewUrlRef = useRef(null);
  const bulkZipParsedRef = useRef(null);
  const previewLoadIdRef = useRef(0);
  const resumeBulkJobRef = useRef(null);
  const bulkZipJobIdRef = useRef(null);
  const bulkZipFetchInflightRef = useRef(null);
  const processingAbortRef = useRef(null);
  const activeBulkJobIdRef = useRef(null);
  const processingPromiseRef = useRef(null);

  const beginProcessingSession = useCallback(() => {
    processingAbortRef.current?.abort();
    processingAbortRef.current = new AbortController();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const revokePreviewUrl = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
  }, []);

  const reset = useCallback(async () => {
    await abortAllServerJobs();
    processingAbortRef.current?.abort();
    processingAbortRef.current = null;
    activeBulkJobIdRef.current = null;
    setStage('upload');
    setFile(null);
    setExtractedData(null);
    setSummary('');
    setOutputType('all');
    setMasked(false);
    setBulkZipMode(false);
    setBulkPackFiles([]);
    setBulkManifest(null);
    setBulkReviewTab(0);
    setBulkJobProgress(null);
    setBulkBatchStats(null);
    bulkZipBlobRef.current = null;
    bulkManifestRef.current = null;
    extractedDataRef.current = null;
    singlePdfBlobRef.current = null;
    singlePdfFilenameRef.current = null;
    processedOutputFilenameRef.current = null;
    bulkZipParsedRef.current = null;
    bulkZipDownloadNameRef.current = null;
    previewLoadIdRef.current += 1;
    revokePreviewUrl();
    setReviewPanel('data');
    setPreviewTitle('');
    setPreviewLoading(false);
    resumeBulkJobRef.current = null;
    bulkZipJobIdRef.current = null;
    bulkZipFetchInflightRef.current = null;
    processingPromiseRef.current = null;
    clearActiveBulkJob();
  }, [revokePreviewUrl]);

  const handleProcessingCancel = useCallback(async () => {
    processingAbortRef.current?.abort();
    processingAbortRef.current = null;
    await reset();
  }, [reset]);

  const handleBulkPackStart = useCallback(async (files) => {
    resumeBulkJobRef.current = null;
    activeBulkJobIdRef.current = null;
    processingPromiseRef.current = null;
    clearActiveBulkJob();
    beginProcessingSession();
    bulkPackCountRef.current = files.length;
    setBulkPackFiles(files);
    setBulkJobProgress({
      processed: 0,
      total: files.length,
      currentFilename: files[0]?.name ?? null,
      currentStage: 'Starting batch',
    });
    setBulkBatchStats(null);
    setProcessingKey((k) => k + 1);
    setStage('processing');
  }, [beginProcessingSession]);

  const bulkPackApiCall = useCallback(
    () => {
      if (processingPromiseRef.current) {
        return processingPromiseRef.current;
      }
      const resumeJobId = resumeBulkJobRef.current;
      const signal = processingAbortRef.current?.signal;
      const promise = bulkPackZip(bulkPackFiles, outputType, {
        jobId: resumeJobId || undefined,
        useAsyncJob: true,
        bulk: true,
        signal,
        onProgress: (status) => {
          if (status?.job_id) {
            activeBulkJobIdRef.current = status.job_id;
          }
          setBulkJobProgress({
            processed: status.processed ?? 0,
            total: status.total ?? bulkPackCountRef.current,
            currentFilename: status.current_filename ?? null,
            currentStage: status.current_stage ?? null,
          });
        },
      }).finally(() => {
        setBulkJobProgress(null);
        resumeBulkJobRef.current = null;
        processingPromiseRef.current = null;
      });
      processingPromiseRef.current = promise;
      return promise;
    },
    [bulkPackFiles, outputType],
  );

  const handleProcess = useCallback(async (uploadedFile) => {
    resumeBulkJobRef.current = null;
    activeBulkJobIdRef.current = null;
    processingPromiseRef.current = null;
    clearActiveBulkJob();
    beginProcessingSession();
    setFile(uploadedFile);
    setBulkJobProgress({
      processed: 0,
      total: 1,
      currentFilename: uploadedFile?.name ?? null,
      currentStage: 'Starting',
    });
    setProcessingKey((k) => k + 1);
    setStage('processing');
  }, [beginProcessingSession]);

  const processPdfApiCall = useCallback(
    () => {
      if (processingPromiseRef.current) {
        return processingPromiseRef.current;
      }
      // Only resume when explicitly requested - never reuse a stale job id after cancel.
      const resumeJobId = resumeBulkJobRef.current || undefined;
      const signal = processingAbortRef.current?.signal;
      const promise = bulkPackZip(file ? [file] : [], outputType, {
        jobId: resumeJobId,
        useAsyncJob: true,
        bulk: false,
        signal,
        onProgress: (status) => {
          if (status?.job_id) {
            activeBulkJobIdRef.current = status.job_id;
          }
          setBulkJobProgress({
            processed: status.processed ?? 0,
            total: status.total ?? 1,
            currentFilename: status.current_filename ?? file?.name ?? null,
            currentStage: status.current_stage ?? null,
          });
        },
      }).finally(() => {
        processingPromiseRef.current = null;
      });
      processingPromiseRef.current = promise;
      return promise;
    },
    [file, outputType],
  );

  const processingEtaSeconds = useMemo(() => {
    const count = bulkZipMode
      ? (bulkPackFiles.length || bulkPackCountRef.current || 1)
      : 1;
    return Math.max(120, count * 120);
  }, [bulkZipMode, bulkPackFiles.length]);

  const enterBulkReview = useCallback(async (items) => {
    const stats = summarizeBulkZohoStatus(items);
    const successes = manifestSuccessItems(items);
    const failures = manifestFailedItems(items);
    const reviewItems = successes.length > 0
      ? successes
      : (Array.isArray(items) ? items : []).filter(
        (item) => item?.output_filename && item?.extracted,
      );

    if (!reviewItems.length) {
      showToast(
        failures.length > 0
          ? (failures[0].error || 'Batch processing failed.')
          : 'Processing finished but extracted data was not available for review. Check Zoho.',
        'error',
      );
      await reset();
      return;
    }

    bulkManifestRef.current = reviewItems;
    setBulkManifest(reviewItems);
    setBulkBatchStats(stats);
    setBulkReviewTab(0);
    setExtractedData(null);
    extractedDataRef.current = null;
    setSummary('');
    setReviewPanel('data');
    revokePreviewUrl();
    setStage('review');

    const failedNames = [
      ...failures.map((item) => item.filename || 'Unknown file'),
      ...stats.zohoFailed.map((item) => item.filename || 'Unknown file'),
    ];

    if (stats.processFailed.length > 0 || stats.zohoFailed.length > 0) {
      const detail = failedNames.slice(0, 3).join(', ');
      const more = failedNames.length > 3 ? ` (+${failedNames.length - 3} more)` : '';
      showToast(
        `${formatBulkZohoSummary(stats)}${detail ? `: ${detail}${more}` : ''}`,
        'error',
      );
    } else if (stats.sheetSkipped.length > 0) {
      showToast(
        `${formatBulkZohoSummary(stats)}. Duplicate settlements were not added again to the sheet.`,
        'success',
      );
    } else if (stats.zohoSaved.length > 0) {
      showToast(
        `${stats.zohoSaved.length} file(s) saved to Zoho (original PDF, processed PDF, and sheet). Download is optional.`,
        'success',
      );
    }
  }, [reset, revokePreviewUrl]);

  const finishBulkReview = useCallback(async (result) => {
    const normalized = normalizeAsyncJobResult(result);
    const jobId = normalized.jobId;
    let items = normalized.manifest?.items;
    if (!Array.isArray(items) || items.length === 0) {
      return false;
    }

    if (jobId) {
      items = await ensureFullManifestItems(jobId, items);
    }
    if (!items.length || !manifestItemsHaveReviewData(items)) {
      return false;
    }

    if (normalized.blob instanceof Blob) {
      bulkZipBlobRef.current = normalized.blob;
    }
    if (jobId) {
      bulkZipJobIdRef.current = jobId;
      persistActiveBulkJob({
        jobId,
        total: items.length,
        outputType,
        zipPending: !(normalized.blob instanceof Blob),
        review: true,
        bulk: true,
      });
    }
    bulkZipDownloadNameRef.current =
      normalized.zipFilename || bulkZipFilename(outputType);
    await enterBulkReview(items);
    return true;
  }, [enterBulkReview, outputType]);

  const finishSingleFileReview = useCallback(async (result) => {
    const normalized = normalizeAsyncJobResult(result);
    const jobId = normalized.jobId;
    let items = normalized.manifest?.items;
    if (!Array.isArray(items) || items.length === 0) {
      return false;
    }

    if (jobId) {
      items = await ensureFullManifestItems(jobId, items);
    }
    if (!items.length || !manifestItemsHaveReviewData(items)) {
      return false;
    }

    if (normalized.blob instanceof Blob) {
      bulkZipBlobRef.current = normalized.blob;
      bulkZipDownloadNameRef.current =
        normalized.zipFilename || bulkZipFilename(outputType);
    }
    if (jobId) {
      bulkZipJobIdRef.current = jobId;
      persistActiveBulkJob({
        jobId,
        total: 1,
        outputType,
        zipPending: !(normalized.blob instanceof Blob),
        review: true,
        bulk: false,
      });
    }

    const successes = manifestSuccessItems(items);
    const failures = manifestFailedItems(items);
    if (failures.length > 0 && successes.length === 0) {
      showToast(failures[0].error || 'Processing failed.', 'error');
      await reset();
      return true;
    }

    const item = successes[0] || items[0];
    if (item?.error) {
      showToast(item.error, 'error');
      await reset();
      return true;
    }
    if (!item?.extracted) {
      return false;
    }
    if (item.zoho_error) {
      showToast(
        `Document processed but Zoho save failed: ${item.zoho_error}`,
        'error',
      );
    }

    extractedDataRef.current = item.extracted;
    setExtractedData(item.extracted);
    setSummary(item.summary || '');
    processedOutputFilenameRef.current =
      manifestPdfArcname(item) || outputPdfFilename(file?.name, outputType);
    singlePdfBlobRef.current = null;
    singlePdfFilenameRef.current = null;
    setReviewPanel('data');
    setStage('review');
    if (!item.zoho_error) {
      showToast(
        'Original PDF, processed PDF, and sheet row saved to Zoho.',
        'success',
      );
    }
    return true;
  }, [file, outputType, reset]);

  const ensureBulkZipBlob = useCallback(async () => {
    if (bulkZipBlobRef.current) {
      return bulkZipBlobRef.current;
    }
    if (bulkZipFetchInflightRef.current) {
      return bulkZipFetchInflightRef.current;
    }
    const jobId = bulkZipJobIdRef.current || getActiveBulkJob()?.jobId;
    if (!jobId) {
      throw new Error('Batch ZIP not available. Process the batch again.');
    }
    const manifest = bulkManifestRef.current?.length
      ? { output_type: outputType, items: bulkManifestRef.current }
      : null;
    const promise = fetchBulkJobZip(jobId, manifest).then((result) => {
      bulkZipBlobRef.current = result.blob;
      bulkZipDownloadNameRef.current = result.zipFilename || bulkZipFilename(outputType);
      bulkZipJobIdRef.current = jobId;
      return result.blob;
    }).finally(() => {
      if (bulkZipFetchInflightRef.current === promise) {
        bulkZipFetchInflightRef.current = null;
      }
    });
    bulkZipFetchInflightRef.current = promise;
    return promise;
  }, [outputType]);

  const loadPreviewPdf = useCallback(async (bulkIndex) => {
    const loadId = ++previewLoadIdRef.current;
    setPreviewLoading(true);
    revokePreviewUrl();
    try {
      let pdfBlob;
      let title;

      if (bulkManifest?.length) {
        const idx =
          typeof bulkIndex === 'number'
            ? bulkIndex
            : Math.min(bulkReviewTab, bulkManifest.length - 1);
        const item = bulkManifest[idx];
        const arcname = manifestPdfArcname(item);
        if (!arcname) {
          showToast('Processed PDF name missing for this file.', 'error');
          return;
        }
        const jobId = bulkZipJobIdRef.current || getActiveBulkJob()?.jobId;
        if (jobId) {
          pdfBlob = await fetchBulkJobPdf(jobId, arcname);
        } else if (bulkZipBlobRef.current) {
          if (!bulkZipParsedRef.current || bulkZipParsedRef.current.blob !== bulkZipBlobRef.current) {
            bulkZipParsedRef.current = {
              blob: bulkZipBlobRef.current,
              zip: await loadZipFromBlob(bulkZipBlobRef.current),
            };
          }
          pdfBlob = await readPdfFromZip(bulkZipParsedRef.current.zip, arcname);
          if (!pdfBlob) {
            showToast(`Could not find ${arcname} in the batch ZIP.`, 'error');
            return;
          }
        } else {
          showToast('Batch preview unavailable. Use Download ZIP or process the batch again.', 'error');
          return;
        }
        title = arcname || outputPdfFilename(item.filename, outputType);
      } else {
        if (!file) {
          showToast('Original file not available. Process the document again.', 'error');
          return;
        }
        title = processedOutputFilenameRef.current
          || outputPdfFilename(file.name, outputType);
        if (!singlePdfBlobRef.current) {
          const zipBlob = bulkZipBlobRef.current;
          const jobId = bulkZipJobIdRef.current || getActiveBulkJob()?.jobId;
          if (jobId) {
            const fromJob = await fetchBulkJobPdf(jobId, title);
            singlePdfBlobRef.current = fromJob;
            singlePdfFilenameRef.current = title;
          } else if (zipBlob) {
            if (!bulkZipParsedRef.current || bulkZipParsedRef.current.blob !== zipBlob) {
              bulkZipParsedRef.current = { blob: zipBlob, zip: await loadZipFromBlob(zipBlob) };
            }
            const fromZip = await readPdfFromZip(bulkZipParsedRef.current.zip, title);
            if (!fromZip) {
              throw new Error('Processed PDF not found. Process the document again.');
            }
            singlePdfBlobRef.current = fromZip;
            singlePdfFilenameRef.current = title;
          } else {
            const result = await generatePDF(file, outputType, {
              extracted: extractedDataRef.current ?? extractedData,
              summary,
            });
            singlePdfBlobRef.current = result.blob;
            singlePdfFilenameRef.current = result.filename || title;
          }
        }
        pdfBlob = singlePdfBlobRef.current;
        title = singlePdfFilenameRef.current || title;
      }

      if (loadId !== previewLoadIdRef.current) return;

      const url = await createPdfPreviewUrl(pdfBlob, title);
      previewUrlRef.current = url;
      setPreviewUrl(url);
      setPreviewTitle(title);
    } catch (err) {
      if (loadId !== previewLoadIdRef.current) return;
      const message = err.message || err.response?.data?.detail || 'Failed to load PDF preview.';
      showToast(typeof message === 'string' ? message : 'Failed to load PDF preview.', 'error');
    } finally {
      if (loadId === previewLoadIdRef.current) {
        setPreviewLoading(false);
      }
    }
  }, [
    bulkManifest,
    bulkReviewTab,
    extractedData,
    file,
    outputType,
    revokePreviewUrl,
    summary,
    ensureBulkZipBlob,
  ]);

  const switchToPreviewPanel = useCallback(() => {
    setReviewPanel('preview');
    loadPreviewPdf();
    requestAnimationFrame(() => {
      document.getElementById('review-preview-panel')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }, [loadPreviewPdf]);

  useEffect(() => {
    if (reviewPanel === 'preview' && bulkManifest?.length) {
      loadPreviewPdf(bulkReviewTab);
    }
  }, [bulkReviewTab, bulkManifest, reviewPanel, loadPreviewPdf]);

  useEffect(() => () => revokePreviewUrl(), [revokePreviewUrl]);

  // Called by ProcessingPanel when API resolves (success or failure)
  const handleProcessingDone = useCallback(async (result, error) => {
    const normalized = normalizeAsyncJobResult(result);

    if (error) {
      showToast(error, 'error');
      await reset();
      return;
    }

    if (bulkZipMode) {
      if (await finishBulkReview(normalized)) {
        return;
      }
      if (normalized.blob instanceof Blob) {
        showToast(
          'Batch ZIP downloaded but review data was missing. Files are saved to Zoho.',
          'error',
        );
        await reset();
        return;
      }
      showToast('Batch finished but review data was unavailable. Files may be saved to Zoho.', 'error');
      await reset();
      return;
    }

    if (await finishSingleFileReview(normalized)) {
      return;
    }

    showToast('Processing completed but review data was unavailable. Files may be saved to Zoho.', 'error');
    await reset();
  }, [bulkZipMode, finishBulkReview, finishSingleFileReview, reset]);

  const handleDownload = async () => {
    const isBulkReview = Boolean(bulkManifest?.length);

    if (isBulkReview) {
      setDownloading(true);
      try {
        const blob = bulkZipBlobRef.current || await ensureBulkZipBlob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = bulkZipDownloadNameRef.current || bulkZipFilename(outputType);
        a.click();
        URL.revokeObjectURL(url);
        setStage('export');
        showToast('ZIP downloaded successfully.', 'success');
      } catch (err) {
        const message = err?.message || 'Failed to download ZIP. Please try again.';
        showToast(message, 'error');
      } finally {
        setDownloading(false);
      }
      return;
    }

    setDownloading(true);
    try {
      let blob;
      let filename = processedOutputFilenameRef.current
        || outputPdfFilename(file?.name, outputType);
      const zipBlob = bulkZipBlobRef.current;
      const jobId = bulkZipJobIdRef.current || getActiveBulkJob()?.jobId;
      if (jobId) {
        blob = await fetchBulkJobPdf(jobId, filename);
      } else if (zipBlob) {
        if (!bulkZipParsedRef.current || bulkZipParsedRef.current.blob !== zipBlob) {
          bulkZipParsedRef.current = { blob: zipBlob, zip: await loadZipFromBlob(zipBlob) };
        }
        const fromZip = await readPdfFromZip(bulkZipParsedRef.current.zip, filename);
        if (!fromZip) {
          throw new Error('Processed PDF not found in archive. Process the document again.');
        }
        blob = fromZip;
      } else {
        const generated = await generatePDF(file, outputType, {
          extracted: extractedDataRef.current ?? extractedData,
          summary,
        });
        blob = generated.blob;
        filename = generated.filename || filename;
      }
      singlePdfBlobRef.current = blob;
      singlePdfFilenameRef.current = filename;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      setStage('export');
      showToast('PDF downloaded successfully.', 'success');
    } catch (err) {
      const message = err.message || err.response?.data?.detail || 'Failed to generate PDF. Please try again.';
      showToast(message, 'error');
    } finally {
      setDownloading(false);
    }
  };

  const currentStageIndex = STAGES.indexOf(stage);

  const bulkTabIndex =
    bulkManifest && bulkManifest.length > 0
      ? Math.min(bulkReviewTab, bulkManifest.length - 1)
      : 0;
  const bulkTabItem =
    bulkManifest && bulkManifest.length > 0 ? bulkManifest[bulkTabIndex] : null;

  return (
    <div style={styles.app}>
      {/* Top bar */}
      <header className="app-topbar" style={styles.topBar}>
        <div style={styles.logo}>
          <div className="app-topbar__logo-badge">
            <img
              src={`${process.env.PUBLIC_URL}/sml-logo.png`}
              alt="Settle My Loan"
            />
          </div>
          <div className="app-topbar__subtitle">
            financial document<br />processor
          </div>
        </div>

        <div style={styles.topBarRight}>
          <div style={styles.stepNav}>
            {STAGE_LABELS.map((label, i) => {
              const isDone = i < currentStageIndex;
              const isActive = i === currentStageIndex;
              const labelClass = isActive
                ? 'app-topbar__step-label app-topbar__step-label--active'
                : isDone
                  ? 'app-topbar__step-label app-topbar__step-label--done'
                  : 'app-topbar__step-label app-topbar__step-label--idle';
              return (
                <React.Fragment key={label}>
                  <div style={styles.stepItem}>
                    <div style={{
                      ...styles.stepDot,
                      borderColor: isDone
                        ? 'var(--green)'
                        : isActive
                          ? 'var(--blue)'
                          : 'var(--border-active)',
                      background: isDone
                        ? 'var(--green)'
                        : isActive
                          ? 'var(--blue)'
                          : 'var(--white)',
                      color: isDone || isActive
                        ? 'var(--white)'
                        : 'var(--blue-soft)',
                    }}>
                      {isDone ? '✓' : i + 1}
                    </div>
                    <span className={labelClass} style={styles.stepLabel}>
                      {label}
                    </span>
                  </div>
                  {i < STAGE_LABELS.length - 1 && (
                    <div style={{
                      ...styles.stepDivider,
                      background: isDone ? 'var(--green)' : 'var(--border)',
                    }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          {onLogout && (
            <button
              type="button"
              className="app-topbar__logout"
              onClick={() => setShowLogoutConfirm(true)}
            >
              Log out
            </button>
          )}
        </div>
      </header>

      {/* Main layout */}
      <div style={styles.main}>
        {/* Left sidebar */}
        <div style={styles.left}>
          <div style={styles.pageTitle}>
            Financial<br />Document<br />Processor
          </div>
          <div style={styles.pageDesc}>
            Upload settlement letters (PDF, PNG, Word, or EML) to extract structured fields, generate summaries, and mask sensitive data. Files and data are saved to Zoho WorkDrive and Zoho Sheet automatically. Bulk mode processes up to 20 files at once - any mix of file types, in All formats / Watermarked / Masked / Standard.
          </div>

          <div style={styles.sideInfo}>
            <div style={styles.sideLabel}>Saved to Zoho Sheet</div>
            <ul style={styles.sideList}>
              {SHEET_COLUMN_LABELS.map((f) => (
                <li key={f} style={styles.sideListItem}>
                  <div style={styles.sideListDot} />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          <div style={{ marginTop: 28 }}>
            <div style={styles.sideLabel}>Workflow</div>
            <ul style={styles.sideList}>
              {['Single file or bulk ZIP', 'Choose format before processing', 'All formats / Watermarked / Masked / Standard', 'Auto-saved to Zoho on completion'].map(f => (
                <li key={f} style={styles.sideListItem}>
                  <div style={styles.sideListDot} />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right content */}
        <div style={styles.right}>

          {stage === 'upload' && (
            <div className="app-content-card">
              <div className="status-pill">
                <div className="status-pill__dot" />
                ready
              </div>

              <OutputOptions
                sectionLabel="download format"
                selected={outputType}
                onChange={setOutputType}
              />

              <label
                style={{
                  ...styles.bulkToggleRow,
                  ...(bulkZipMode ? styles.bulkToggleRowActive : {}),
                }}
              >
                <input
                  type="checkbox"
                  checked={bulkZipMode}
                  onChange={(e) => {
                    const v = e.target.checked;
                    setBulkZipMode(v);
                    if (!v) setBulkPackFiles([]);
                  }}
                  style={{ marginTop: 3, accentColor: 'var(--accent)' }}
                />
                <div style={styles.bulkToggleText}>
                  <div style={styles.bulkToggleTitle}>Bulk batch - PDF, PNG, Word, or EML</div>
                  <div style={styles.bulkToggleDesc}>
                    Upload up to 20 files in one batch (any mix of PDF, PNG, Word, EML). Each file is processed with the <strong>download format</strong> you selected above - All formats, Watermarked, Masked, or Standard. Saved to Zoho WorkDrive and Sheet automatically. Optional download as <strong>{bulkZipFilename(outputType)}</strong>.
                  </div>
                </div>
              </label>

              <UploadZone
                key={bulkZipMode ? 'bulk' : 'single'}
                bulkAllFormats={bulkZipMode}
                onProcessSingle={handleProcess}
                onBulkPack={handleBulkPackStart}
              />
            </div>
          )}

          {stage === 'processing' && (
            <div className="app-content-card">
            <ProcessingPanel
              key={processingKey}
              apiCall={bulkZipMode ? bulkPackApiCall : processPdfApiCall}
              onDone={handleProcessingDone}
              onCancel={handleProcessingCancel}
              estimatedSeconds={processingEtaSeconds}
              fileProgress={bulkJobProgress}
            />
            </div>
          )}

          {(stage === 'review' || stage === 'export') && (extractedData || (bulkManifest && bulkManifest.length > 0)) && (
            <div className="app-content-card">
              <div className="status-pill">
                <div className="status-pill__dot status-pill__dot--success" />
                extracted
              </div>

              <div className="review-panel-strip" role="tablist" aria-label="Review view">
                <button
                  type="button"
                  role="tab"
                  aria-selected={reviewPanel === 'data'}
                  className={`review-panel-tab${reviewPanel === 'data' ? ' review-panel-tab--active' : ''}`}
                  onClick={() => setReviewPanel('data')}
                >
                  Extracted data
                  {reviewPanel === 'data' ? (
                    <span className="review-panel-tab__indicator" aria-hidden="true" />
                  ) : null}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={reviewPanel === 'preview'}
                  className={`review-panel-tab${reviewPanel === 'preview' ? ' review-panel-tab--active' : ''}`}
                  onClick={switchToPreviewPanel}
                >
                  PDF preview
                  {reviewPanel === 'preview' ? (
                    <span className="review-panel-tab__indicator" aria-hidden="true" />
                  ) : null}
                </button>
              </div>

              {bulkManifest && bulkManifest.length > 0 ? (
                <>
                  {bulkBatchStats ? (
                    <div
                      style={{
                        ...styles.bulkStatusBanner,
                        ...(bulkBatchStats.processFailed.length > 0 || bulkBatchStats.zohoFailed.length > 0
                          ? styles.bulkStatusBannerWarn
                          : {}),
                      }}
                    >
                      <div style={styles.bulkStatusTitle}>Batch results</div>
                      <div>{formatBulkZohoSummary(bulkBatchStats)}</div>
                      {(bulkBatchStats.processFailed.length > 0
                        || bulkBatchStats.zohoFailed.length > 0
                        || bulkBatchStats.sheetSkipped.length > 0) ? (
                        <ul style={styles.bulkStatusList}>
                          {bulkBatchStats.sheetSkipped.map((item) => (
                            <li key={`sk-${item.filename}`} style={styles.bulkStatusListItem}>
                              <strong>{item.filename || 'Unknown file'}</strong>
                              {' - duplicate sheet row skipped (already in Zoho Sheet)'}
                            </li>
                          ))}
                          {bulkBatchStats.processFailed.map((item) => (
                            <li key={`pf-${item.filename}`} style={styles.bulkStatusListItem}>
                              <strong>{item.filename || 'Unknown file'}</strong>
                              {' - processing failed: '}
                              {item.error || 'Unknown error'}
                            </li>
                          ))}
                          {bulkBatchStats.zohoFailed.map((item) => (
                            <li key={`zf-${item.filename}`} style={styles.bulkStatusListItem}>
                              <strong>{item.filename || 'Unknown file'}</strong>
                              {' - Zoho save failed: '}
                              {item.zoho_error || 'Unknown error'}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="bulk-tab-strip" role="tablist" aria-label="Documents in batch">
                    {bulkManifest.map((item, idx) => {
                      const fullLabel = item.filename || `File ${idx + 1}`;
                      const short =
                        fullLabel.length > 28 ? `${fullLabel.slice(0, 25)}…` : fullLabel;
                      const active = bulkTabIndex === idx;
                      return (
                        <div
                          key={`${item.folder || item.filename || 'f'}-${idx}`}
                          role="tab"
                          tabIndex={0}
                          aria-selected={active}
                          title={fullLabel}
                          className={`bulk-tab${active ? ' bulk-tab--active' : ''}`}
                          onClick={() => setBulkReviewTab(idx)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setBulkReviewTab(idx);
                            }
                          }}
                        >
                          <span className="bulk-tab__label">{short}</span>
                          {active ? (
                            <span className="bulk-tab__indicator" aria-hidden="true" />
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                  <div style={styles.bulkTabPanel} role="tabpanel">
                    {reviewPanel === 'data' && bulkTabItem ? (
                      <ExtractedData
                        data={bulkTabItem.extracted}
                        summary={bulkTabItem.summary}
                        masked={masked}
                        showMaskToggle={showMaskToggle}
                        onMaskToggle={() => setMasked((m) => !m)}
                      />
                    ) : null}
                    {reviewPanel === 'preview' ? (
                      <PdfPreviewPanel
                        url={previewUrl}
                        loading={previewLoading}
                        title={previewTitle}
                        formatLabel={OUTPUT_TYPE_LABELS[outputType]}
                        onOpenNewTab={() => {
                          if (previewUrl) window.open(previewUrl, '_blank', 'noopener,noreferrer');
                        }}
                      />
                    ) : null}
                  </div>
                </>
              ) : reviewPanel === 'data' ? (
                <ExtractedData
                  data={extractedData}
                  summary={summary}
                  masked={masked}
                  showMaskToggle={showMaskToggle}
                  onMaskToggle={() => setMasked((m) => !m)}
                />
              ) : (
                <PdfPreviewPanel
                  url={previewUrl}
                  loading={previewLoading}
                  title={previewTitle}
                  formatLabel={OUTPUT_TYPE_LABELS[outputType]}
                  onOpenNewTab={() => {
                    if (previewUrl) window.open(previewUrl, '_blank', 'noopener,noreferrer');
                  }}
                />
              )}

              <div style={styles.divider} />

              <div style={styles.outputChoiceReminder}>
                {bulkManifest && bulkManifest.length > 0 ? (
                  <>
                    Zoho is already updated for every file (original PDF, processed PDF, and sheet row). Download is optional - preview or save a local ZIP - format:{' '}
                    <span style={styles.outputChoiceReminderStrong}>{OUTPUT_TYPE_LABELS[outputType]}</span>
                    {' '}(one PDF per file).
                  </>
                ) : (
                  <>
                    Zoho is already updated (original PDF, processed PDF, and sheet row). Download is optional - preview or save a local copy - format:{' '}
                    <span style={styles.outputChoiceReminderStrong}>{OUTPUT_TYPE_LABELS[outputType]}</span>.
                  </>
                )}
              </div>

              <div style={styles.actionRow}>
                <button
                  style={{
                    ...styles.btnPrimary,
                    ...(downloading ? styles.btnLoading : {}),
                  }}
                  onClick={handleDownload}
                  disabled={downloading}
                  onMouseOver={(e) => !downloading && (e.currentTarget.style.opacity = '0.85')}
                  onMouseOut={(e) => (e.currentTarget.style.opacity = '1')}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  {downloading
                    ? 'Downloading...'
                    : bulkManifest?.length
                      ? 'Download ZIP'
                      : 'Download PDF'}
                </button>
              </div>

              <button
                style={styles.resetBtn}
                onClick={reset}
                onMouseOver={(e) => (e.target.style.color = 'var(--text-secondary)')}
                onMouseOut={(e) => (e.target.style.color = 'var(--text-tertiary)')}
              >
                ← process another document
              </button>
            </div>
          )}
        </div>
      </div>

      <PageFooter />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {showLogoutConfirm && (
        <ConfirmDialog
          title="Log out?"
          message="Do you want to log out?"
          confirmLabel="Yes"
          cancelLabel="No"
          onConfirm={async () => {
            setShowLogoutConfirm(false);
            await onLogout();
          }}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}
    </div>
  );
}

const AppWithAuth = () => (
  <LoginGate>
    <App />
  </LoginGate>
);

export default AppWithAuth;